import React, { useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';

const INCREASE_TYPES = [
  { id: 'borrow', name: 'Borrow' },
  { id: 'gift', name: 'Gift' },
  { id: 'manual', name: 'Manual Adjustment' },
];

const DECREASE_TYPES = [
  { id: 'sale', name: 'Sale' },
  { id: 'damage', name: 'Damage' },
  { id: 'manual', name: 'Manual Adjustment' },
];

export default function InventoryChangeModal({ item, type, onChange, onClose }) {
  const [formData, setFormData] = useState({
    inventory_id: item.id,
    quantity_change: '',
    change_type: type === 'increase' ? 'borrow' : 'sale',
    notes: '',
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = null;

      // Upload image if selected
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);

        const uploadResponse = await fetch('/api/images/upload', {
          method: 'POST',
          headers: {
            'X-Employee-ID': localStorage.getItem('employeeId'),
          },
          body: imageFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        const uploadData = await uploadResponse.json();
        imageUrl = uploadData.url;
      }

      // Calculate actual quantity change
      const quantityChange = type === 'increase' 
        ? Math.abs(parseFloat(formData.quantity_change))
        : -Math.abs(parseFloat(formData.quantity_change));

      // Submit inventory change
      await onChange({
        inventory_id: formData.inventory_id,
        quantity_change: quantityChange,
        change_type: formData.change_type,
        notes: formData.notes,
        image_url: imageUrl,
      });
    } catch (error) {
      console.error('Error submitting inventory change:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
          <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {type === 'increase' ? 'Increase' : 'Decrease'} Inventory: {item.name}
              </h3>
              <div className="mt-2">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="change_type" className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      id="change_type"
                      name="change_type"
                      required
                      value={formData.change_type}
                      onChange={(e) => setFormData({ ...formData, change_type: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      {(type === 'increase' ? INCREASE_TYPES : DECREASE_TYPES).map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="quantity_change" className="block text-sm font-medium text-gray-700">
                      Quantity ({item.unit})
                    </label>
                    <input
                      type="number"
                      name="quantity_change"
                      id="quantity_change"
                      required
                      min="0.01"
                      step="0.01"
                      value={formData.quantity_change}
                      onChange={(e) => setFormData({ ...formData, quantity_change: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Image</label>
                    <div className="mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                      <div className="space-y-1 text-center">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="mx-auto h-32 w-32 object-cover" />
                        ) : (
                          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                        )}
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="image"
                            className="relative cursor-pointer rounded-md bg-white font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500"
                          >
                            <span>Upload a file</span>
                            <input
                              id="image"
                              name="image"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={handleImageChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={uploading}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {uploading ? 'Processing...' : type === 'increase' ? 'Increase' : 'Decrease'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={uploading}
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 